<template>
  <div class="ca-tab">
    <!-- ================== Engine ================== -->
    <div class="gh-layout">
      <aside class="gh-intro">
        <h2 class="gh-intro__title">
          Engine
          <span class="gh-intro__badge">Audit</span>
        </h2>
        <p class="gh-intro__desc">
          Which accessibility engines run against this client's pages. The Town Web
          Accessibility Checker always runs; add IBM (code, WCAG 2.2 AA) and/or AXE
          (axe-core). Each engine's findings are merged.
        </p>
        <!-- Old ADA Auditor page (its Tools & Insights card is hidden; route kept). -->
        <router-link class="ca-intro-link" :to="'/juniper/clients/edit/' + clientId + '/ada-auditor'">
          <i class="fas fa-universal-access"></i>
          Open ADA Auditor
          <i class="fas fa-chevron-right ca-intro-link__arrow"></i>
        </router-link>
      </aside>
      <section class="gh-accounts">
        <div class="ca-target-group">
          <label
            v-for="opt in engineOptions"
            :key="opt.value"
            class="ca-target-opt"
            :class="{ 'ca-target-opt--active': selectedEngines.includes(opt.value) }"
          >
            <input v-model="selectedEngines" type="checkbox" :value="opt.value" :disabled="opt.value === 'TWAC'" />
            <span class="ca-check" :class="{ 'ca-check--on': selectedEngines.includes(opt.value) }">
              <i v-if="selectedEngines.includes(opt.value)" class="fas fa-check"></i>
            </span>
            <i :class="opt.icon"></i>
            <div>
              <div class="ca-target-opt__name">{{ opt.label }}</div>
              <div class="ca-target-opt__sub">{{ opt.sub }}</div>
            </div>
          </label>
        </div>
      </section>
    </div>

    <hr class="gh-divider" />

    <!-- ================== Automatic Scan Interval ================== -->
    <div class="gh-layout">
      <aside class="gh-intro">
        <h2 class="gh-intro__title">
          Scan Interval
          <span class="gh-intro__badge">Audit</span>
        </h2>
        <p class="gh-intro__desc">
          How often an audit runs automatically. "Off" means audits only run when
          started manually.
        </p>
      </aside>
      <section class="gh-accounts">
        <div class="ca-target-group">
          <label
            v-for="opt in intervalOptions"
            :key="opt.value"
            class="ca-target-opt"
            :class="{ 'ca-target-opt--active': settings.audit_scan_interval === opt.value }"
          >
            <input v-model="settings.audit_scan_interval" type="radio" :value="opt.value" />
            <i :class="opt.icon"></i>
            <div>
              <div class="ca-target-opt__name">{{ opt.label }}</div>
              <div class="ca-target-opt__sub">{{ opt.sub }}</div>
            </div>
            <button
              v-if="['daily', 'weekly', 'monthly'].includes(opt.value)"
              type="button"
              class="ca-gear"
              title="Set run day &amp; time"
              @click.prevent="openSchedule(opt.value)"
            >
              <i class="fas fa-cog"></i>
            </button>
          </label>
        </div>
        <!-- Compact schedule summary; the gear on a card opens the picker modal. -->
        <div v-if="showSchedulePickers" class="ca-schedule-summary">
          <i class="fas fa-clock"></i>
          <span>Runs {{ scheduleSummary }}</span>
          <button type="button" class="ca-schedule-summary__edit" @click="openSchedule(settings.audit_scan_interval)">Change</button>
          <span v-if="slotConflicts.length" class="ca-schedule-summary__conflict" :title="slotConflicts.join(', ')">
            <i class="fas fa-exclamation-triangle"></i> shared slot
          </span>
        </div>
        <div class="ca-toggle-row">
          <label class="ca-toggle">
            <input v-model="settings.audit_only_changed" type="checkbox" />
            <span class="ca-toggle__track"><span class="ca-toggle__thumb"></span></span>
            <span class="ca-toggle__label">
              Check only changed pages &amp; files
              <small class="d-block text-muted">Scheduled audits skip unchanged content.</small>
            </span>
          </label>
        </div>
      </section>
    </div>

    <hr class="gh-divider" />

    <!-- ================== Email Report ================== -->
    <div class="gh-layout">
      <aside class="gh-intro">
        <h2 class="gh-intro__title">
          Email Report
          <span class="gh-intro__badge">Audit</span>
        </h2>
        <p class="gh-intro__desc">
          Send a summary email when an audit report finishes.
        </p>
      </aside>
      <section class="gh-accounts">
        <div class="ca-toggle-row ca-toggle-row--first">
          <label class="ca-toggle">
            <input v-model="settings.audit_email_enabled" type="checkbox" />
            <span class="ca-toggle__track"><span class="ca-toggle__thumb"></span></span>
            <span class="ca-toggle__label">
              Email report when finished
              <small class="d-block text-muted">A report summary is emailed after every finished audit.</small>
            </span>
          </label>
        </div>
        <template v-if="settings.audit_email_enabled">
          <div v-if="isUnifiedDashboard" class="ca-email-note">
            <i class="fas fa-info-circle"></i>
            This client uses the Unified Dashboard — the report is emailed to all HeyGov
            admins of the jurisdiction.
          </div>
          <div v-else class="ca-email-recipients">
            <label>Recipients</label>
            <input
              v-model="settings.audit_email_recipients"
              type="text"
              class="form-control"
              placeholder="mail@example.com, other@example.com"
              autocomplete="off"
              spellcheck="false"
            />
            <small class="text-muted">Comma-separated email addresses. The email is sent from Juniper when the audit finishes.</small>
          </div>
        </template>
        <div class="ca-test-email-row">
          <button type="button" class="gh-save-btn" @click="openTestEmail">
            <i class="fas fa-paper-plane"></i>
            Send Test Email
          </button>
          <small class="text-muted d-block">Sends a sample report email so you can preview the design.</small>
        </div>
      </section>
    </div>

    <hr class="gh-divider" />

    <!-- ================== Slack ================== -->
    <div class="gh-layout">
      <aside class="gh-intro">
        <h2 class="gh-intro__title">
          Slack
          <span class="gh-intro__badge">Audit</span>
        </h2>
        <p class="gh-intro__desc">
          Post a report summary to Slack when an audit finishes. The channel webhook
          is configured globally in Juniper Settings &rarr; ADA Compliance.
        </p>
      </aside>
      <section class="gh-accounts">
        <div class="ca-toggle-row ca-toggle-row--first">
          <label class="ca-toggle">
            <input v-model="settings.audit_slack_enabled" type="checkbox" />
            <span class="ca-toggle__track"><span class="ca-toggle__thumb"></span></span>
            <span class="ca-toggle__label">
              Enable Slack notifications
              <small class="d-block text-muted">Score, pages, compliant/not compliant, errors and warnings are posted to the configured channel.</small>
            </span>
          </label>
        </div>
      </section>
    </div>

    <hr class="gh-divider" />

    <!-- ================== Reports ================== -->
    <div class="gh-layout">
      <aside class="gh-intro">
        <h2 class="gh-intro__title">
          Reports
          <span class="gh-intro__badge">Audit</span>
          <span class="cs-count-badge">{{ total }}</span>
        </h2>
        <p class="gh-intro__desc">
          Latest audit reports for this client. Click <strong>Run Audit</strong> to start a new one.
        </p>
      </aside>
      <section class="gh-accounts">
        <!-- Reports toolbar (Run lives here, near the table it produces) -->
        <div class="ca-reports-toolbar">
          <button
            class="ca-btn ca-btn--sm"
            :disabled="running"
            @click="runAudit"
          >
            <i class="fas fa-play"></i>
            {{ running ? 'Starting…' : 'Run Audit' }}
          </button>
          <button
            v-if="reports.length && canBulkDelete"
            class="ca-btn ca-btn--sm ca-btn--danger"
            @click="openBulkDeleteModal"
          >
            <i class="fas fa-trash"></i>
            Delete reports
          </button>
        </div>

        <div class="ada-card">
          <div v-if="loadingReports && reports.length === 0" class="cs-empty-state">
            <i class="fas fa-spinner fa-spin me-2"></i>Loading…
          </div>

          <div v-else-if="reports.length === 0" class="cs-empty-state">
            No reports yet. Click <strong>Run Audit</strong> to start the first one.
          </div>

          <template v-else>
            <table class="cs-subs-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th class="ca-scans-col">Webpages</th>
                  <th>Date</th>
                  <th class="cs-subs-table__score-col">Score</th>
                  <th class="cs-subs-table__actions-col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in reports" :key="r.id" class="ca-row" @click="openReport(r.id)">
                  <td>
                    <span class="ca-report-name">{{ r.name || ('Report #' + r.id) }}</span>
                    <i
                      class="fas ca-row-icon ms-2"
                      :class="isSystemGenerated(r) ? 'fa-cogs' : 'fa-user'"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      :title="triggeredByTooltip(r)"
                    ></i>
                  </td>
                  <td class="ca-scans-col">
                    <div class="ca-scans__row">
                      <i class="fas fa-file-alt ca-row-icon" title="Scanned / total"></i>
                      <span>{{ scansOf(r, 'webpages').scanned }}/{{ scansOf(r, 'webpages').total }}</span>
                      <template v-if="scansOf(r, 'webpages').reused">
                        <i
                          class="fas fa-sync ca-row-icon ms-2"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          :title="scansOf(r, 'webpages').reused + ' reused'"
                        ></i>
                        <span>{{ scansOf(r, 'webpages').reused }}</span>
                      </template>
                    </div>
                  </td>
                  <td>{{ formatDate(r.created_at) }}</td>
                  <td class="text-center">
                    <ScoreCircle v-if="r.status === 'finished'" :percent="r.score" :size="25" :label-scale="0.5" />
                    <div v-else class="ca-progress" :title="progressOf(r) + '%'">
                      <div class="ca-progress__bar" :style="{ width: progressOf(r) + '%' }"></div>
                      <span class="ca-progress__label">{{ progressOf(r) }}%</span>
                    </div>
                  </td>
                  <td class="text-end">
                    <div class="ca-actions">
                      <button
                        v-if="r.status === 'finished'"
                        class="ca-link-icon"
                        :title="'Download report ' + r.id"
                        @click.stop="downloadReport(r)"
                      >
                        <i class="fas fa-download"></i>
                      </button>
                      <button
                        class="ca-link-icon ca-link-icon--danger"
                        :title="'Delete report ' + r.id"
                        @click.stop="askDelete(r)"
                      >
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="reports.length < total" class="cs-load-more-row">
              <button type="button" class="cs-load-more-btn" :disabled="loadingMore" @click="loadMore">
                <i v-if="loadingMore" class="fas fa-spinner fa-spin me-1"></i>
                {{ loadingMore ? 'Loading…' : 'Load more' }}
              </button>
              <small class="text-muted">{{ reports.length }} of {{ total }}</small>
            </div>
            <div v-else-if="total > pageSize" class="cs-load-more-row">
              <small class="text-muted">Showing all {{ total }} reports.</small>
            </div>
          </template>
        </div>
      </section>
    </div>

    <hr class="gh-divider" />

    <!-- ================== Resolutions (audit_warnings acknowledgments) ================== -->
    <div class="gh-layout">
      <aside class="gh-intro">
        <h2 class="gh-intro__title">
          Resolutions
          <span class="cs-count-badge">{{ ackWarnings.length }}</span>
        </h2>
        <p class="gh-intro__desc">
          Findings dismissed or resolved for this client — they no longer appear in new scans.
          Click a row to see what the rule flags and the note that was left. Removing an entry
          makes the finding surface again on the next scan.
        </p>
      </aside>
      <section class="gh-accounts">
        <div class="ada-card">
          <div v-if="loadingAcks" class="cs-empty-state">
            <i class="fas fa-spinner fa-spin me-2"></i>Loading…
          </div>
          <div v-else-if="ackWarnings.length === 0" class="cs-empty-state">
            Nothing dismissed or resolved for this client yet.
          </div>

          <template v-else>
            <table class="cs-subs-table ca-acks-table">
              <thead>
                <tr>
                  <th>Engine</th>
                  <th>Rule</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Note</th>
                  <th>Added</th>
                  <th class="cs-subs-table__actions-col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="w in visibleAckWarnings" :key="'ack-' + w.id" class="ca-row" @click="openAckDetails(w)">
                  <td><span class="ca-ack-engine">{{ w.engine }}</span></td>
                  <td>
                    <code class="ca-ack-rule">{{ w.rule_id }}</code>
                    <div v-if="w.url" class="ca-ack-url">{{ w.url }}</div>
                  </td>
                  <td>
                    <span class="ca-ack-badge" :class="w.bucket === 'error' ? 'ca-ack-badge--error' : 'ca-ack-badge--warn'">
                      {{ w.bucket }}
                    </span>
                  </td>
                  <td>
                    <span class="ca-ack-badge" :class="w.status === 'resolved' ? 'ca-ack-badge--resolved' : 'ca-ack-badge--dismissed'">
                      {{ w.status }}
                    </span>
                  </td>
                  <td class="ca-ack-note"><span :title="w.note || ''">{{ w.note || '—' }}</span></td>
                  <td class="ca-ack-added">
                    <i
                      class="fas fa-user-circle ca-row-icon"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      :title="ackTooltip(w)"
                    ></i>
                    <span :title="w.created_at || ''">{{ w.created_at ? fromNowUtc(w.created_at) : '—' }}</span>
                  </td>
                  <td class="text-end">
                    <div class="ca-actions">
                      <button
                        class="ca-link-icon ca-link-icon--danger"
                        title="Remove — finding re-surfaces on next scan"
                        @click.stop="askAckDelete(w)"
                      >
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="visibleAckWarnings.length < ackWarnings.length" class="cs-load-more-row">
              <button type="button" class="cs-load-more-btn" @click="loadMoreAcks">Load more</button>
              <small class="text-muted">{{ visibleAckWarnings.length }} of {{ ackWarnings.length }}</small>
            </div>
          </template>
        </div>
      </section>
    </div>

    <!-- ================== Save (bottom-right, like every other tab) ================== -->
    <hr class="gh-divider" />
    <div class="gh-save-row gh-save-row--split">
      <button
        type="button"
        class="ca-help-btn"
        title="Help"
        aria-label="Audit settings help"
        @click="openHelp"
      >
        <i class="fas fa-info-circle"></i>
      </button>
      <button
        class="gh-save-btn"
        :disabled="saving"
        @click="saveSettings"
      >
        <i class="fas fa-save"></i>
        {{ saving ? 'Saving…' : 'Save changes' }}
      </button>
    </div>

    <!-- ================== Report details modal ================== -->
    <ReportDetailsModal
      v-if="activeReportId"
      :report-id="activeReportId"
      :client-id="clientId"
      @close="closeReport"
    />

    <!-- ================== Delete confirmation modal ================== -->
    <!-- Send-test-email modal: preview the report email design -->
    <div class="modal fade" tabindex="-1" ref="testEmailModal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-sm">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Send Test Email</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeTestEmail"></button>
          </div>
          <div class="modal-body">
            <label class="form-label">Send a sample report to</label>
            <input
              v-model.trim="testEmail"
              type="email"
              class="form-control"
              placeholder="you@townweb.com"
              autocomplete="off"
              spellcheck="false"
              @keyup.enter="sendTestEmail"
            />
            <small class="text-muted d-block mt-2">Uses sample data — no real report is read.</small>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="sendingTest" @click="closeTestEmail">Cancel</button>
            <button type="button" class="btn btn-primary" :disabled="sendingTest || !testEmail" @click="sendTestEmail">
              <i v-if="sendingTest" class="fas fa-spinner fa-spin me-1"></i>
              {{ sendingTest ? 'Sending…' : 'Send' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Run-schedule modal: modern chip/grid picker (15-min slots) -->
    <div class="modal fade" tabindex="-1" ref="scheduleModal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Run schedule — {{ intervalTitle }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeSchedule"></button>
          </div>
          <div class="modal-body">
            <template v-if="settings.audit_scan_interval === 'weekly'">
              <div class="ca-picker-label">Day of week</div>
              <div class="ca-chips">
                <button
                  type="button"
                  class="ca-chip"
                  :class="{ 'ca-chip--on': settings.audit_schedule_weekday === '' }"
                  @click="settings.audit_schedule_weekday = ''"
                >Auto ({{ autoWeekdayLabel.slice(0, 3) }})</button>
                <button
                  v-for="(d, i) in weekdayNames"
                  :key="d"
                  type="button"
                  class="ca-chip"
                  :class="{ 'ca-chip--on': settings.audit_schedule_weekday === String(i) }"
                  @click="settings.audit_schedule_weekday = String(i)"
                >{{ d.slice(0, 3) }}</button>
              </div>
            </template>
            <template v-if="settings.audit_scan_interval === 'monthly'">
              <div class="ca-picker-label">Day of month</div>
              <div class="ca-chips">
                <button
                  type="button"
                  class="ca-chip"
                  :class="{ 'ca-chip--on': settings.audit_schedule_monthday === '' }"
                  @click="settings.audit_schedule_monthday = ''"
                >Auto (day {{ autoMonthday }})</button>
              </div>
              <div class="ca-grid ca-grid--days">
                <button
                  v-for="d in 31"
                  :key="'md' + d"
                  type="button"
                  class="ca-chip"
                  :class="{ 'ca-chip--on': settings.audit_schedule_monthday === String(d) }"
                  @click="settings.audit_schedule_monthday = String(d)"
                >{{ d }}</button>
              </div>
              <small v-if="Number(settings.audit_schedule_monthday) >= 29" class="text-muted d-block mb-2">
                Falls on the month's last day when the month is shorter.
              </small>
            </template>
            <div class="ca-picker-label">Time <small class="text-muted">(America/Chicago)</small></div>
            <div class="ca-chips">
              <button
                type="button"
                class="ca-chip"
                :class="{ 'ca-chip--on': settings.audit_schedule_time === '' }"
                @click="settings.audit_schedule_time = ''"
              >Auto ({{ autoSlotLabel }})</button>
            </div>
            <div class="ca-grid ca-grid--hours">
              <button
                v-for="h in 24"
                :key="'h' + h"
                type="button"
                class="ca-chip"
                :class="{ 'ca-chip--on': timeHour === h - 1 }"
                @click="setScheduleHour(h - 1)"
              >{{ String(h - 1).padStart(2, '0') }}</button>
            </div>
            <div class="ca-chips ca-chips--minutes">
              <button
                v-for="m in ['00', '15', '30', '45']"
                :key="'m' + m"
                type="button"
                class="ca-chip"
                :class="{ 'ca-chip--on': timeMinute === m, 'ca-chip--disabled': timeHour === null }"
                @click="setScheduleMinute(m)"
              >:{{ m }}</button>
            </div>
            <div v-if="slotConflicts.length" class="ca-slot-warning">
              <i class="fas fa-exclamation-triangle"></i>
              This slot is already used by:
              <strong>{{ slotConflicts.join(', ') }}</strong> — runs will queue behind each other.
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="closeSchedule">Done</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ================== Bulk delete reports modal ================== -->
    <div class="modal fade" tabindex="-1" ref="bulkDeleteModal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-trash text-danger me-2"></i>
              Delete audit reports
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeBulkDeleteModal"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted mb-3">
              Bulk-delete this client's audit reports with all their page scans.
              Reports still running are never deleted. This cannot be undone.
            </p>
            <div class="mb-3">
              <label class="form-label">Generated by</label>
              <select v-model="bulkScope" class="form-select">
                <option value="all">All reports</option>
                <option value="manual">Manual (started by a person)</option>
                <option value="automatic">Automatic (scheduled / system)</option>
              </select>
            </div>
            <div class="mb-2">
              <label class="form-label">Age</label>
              <select v-model="bulkAge" class="form-select">
                <option value="">Everything (any age)</option>
                <option value="7">Older than 7 days</option>
                <option value="30">Older than 30 days</option>
                <option value="60">Older than 60 days</option>
                <option value="90">Older than 90 days</option>
                <option value="180">Older than 180 days</option>
                <option value="365">Older than 365 days</option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" @click="closeBulkDeleteModal">Cancel</button>
            <button type="button" class="btn btn-danger" :disabled="bulkDeleting" @click="executeBulkDelete">
              <i class="fas me-1" :class="bulkDeleting ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
              {{ bulkDeleting ? 'Deleting…' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" tabindex="-1" ref="deleteConfirmModal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Delete report</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeDeleteModal"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">
              Are you sure you want to delete
              <strong>{{ deleteTarget && deleteTarget.name ? deleteTarget.name : 'this report' }}</strong>?
              This permanently removes the report and all of its scans.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="deleting" @click="closeDeleteModal">
              Cancel
            </button>
            <button type="button" class="btn btn-danger" :disabled="deleting" @click="confirmDelete">
              <i v-if="deleting" class="fas fa-spinner fa-spin me-1"></i>
              {{ deleting ? 'Deleting…' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ================== Resolution details modal (row click drill-down) ================== -->
    <div class="modal fade" tabindex="-1" ref="ackDetailsModal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div class="modal-content" v-if="ackDetailsWarning">
          <div class="modal-header">
            <h6 class="modal-title ca-ackd-title">
              <span class="ca-ack-engine me-2">{{ ackDetailsWarning.engine }}</span>
              <code class="ca-ack-rule">{{ ackDetailsWarning.rule_id }}</code>
              <span class="ca-ack-badge ms-2" :class="ackDetailsWarning.bucket === 'error' ? 'ca-ack-badge--error' : 'ca-ack-badge--warn'">
                {{ ackDetailsWarning.bucket }}
              </span>
              <span class="ca-ack-badge ms-1" :class="ackDetailsWarning.status === 'resolved' ? 'ca-ack-badge--resolved' : 'ca-ack-badge--dismissed'">
                {{ ackDetailsWarning.status }}
              </span>
            </h6>
            <button type="button" class="btn-close" aria-label="Close" @click="closeAckDetails"></button>
          </div>
          <div class="modal-body ca-ackd-body">
            <div v-if="ackDetailsWarning.note" class="ca-ackd-note">
              <i class="fas fa-sticky-note me-1"></i> {{ ackDetailsWarning.note }}
            </div>
            <div class="ca-ackd-meta">
              Added {{ ackDetailsWarning.created_at ? fromNowUtc(ackDetailsWarning.created_at) : '—' }}
              <template v-if="ackDetailsWarning.acknowledged_by"> by {{ ackTooltip(ackDetailsWarning) }}</template>
              <template v-if="ackDetailsWarning.url"> · {{ ackDetailsWarning.url }}</template>
            </div>

            <div v-if="ackDetailsLoading" class="text-muted py-3">
              <i class="fas fa-spinner fa-spin me-1"></i> Loading examples…
            </div>
            <div v-else-if="ackDetailsError" class="text-danger py-3">{{ ackDetailsError }}</div>
            <template v-else>
              <div v-if="ackDetailsScan" class="ca-ackd-scan">
                <i class="fas fa-crosshairs me-1"></i>
                Examples from <a :href="ackDetailsScan.url" target="_blank" rel="noopener">{{ ackDetailsScan.url }}</a>
                <span class="text-muted">(scan #{{ ackDetailsScan.id }})</span>
              </div>

              <div v-if="!ackDetailsExamples.length" class="text-muted py-2">
                No matching finding found in stored scans — the source scan was deleted and no recent scan
                hits this rule anymore.
              </div>
              <ul v-else class="ca-ackd-examples">
                <li v-for="(ex, i) in ackDetailsExamples" :key="i" class="ca-ackd-example">
                  <div class="ca-ackd-example-msg">{{ ex.message || ex.description || '—' }}</div>
                  <div v-if="ackExampleLocation(ex)" class="ca-ackd-example-loc">
                    <code>{{ ackExampleLocation(ex) }}</code>
                  </div>
                  <pre v-if="ackExampleSnippet(ex)" class="ca-ackd-example-snippet">{{ ackExampleSnippet(ex) }}</pre>
                </li>
              </ul>
            </template>
          </div>
          <div class="modal-footer">
            <button type="button" class="ca-btn ca-btn--sm" @click="closeAckDetails">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ================== Resolution delete confirmation ================== -->
    <div class="modal fade" tabindex="-1" ref="ackDeleteModal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Remove resolution</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeAckDelete"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">
              <strong>{{ ackDeleteTarget ? ackDeleteTarget.engine + ' · ' + ackDeleteTarget.rule_id : '' }}</strong>
              will no longer be hidden — the finding re-surfaces on the next scan.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="ackDeleting" @click="closeAckDelete">
              Cancel
            </button>
            <button type="button" class="btn btn-danger" :disabled="ackDeleting" @click="confirmAckDelete">
              <i v-if="ackDeleting" class="fas fa-spinner fa-spin me-1"></i>
              {{ ackDeleting ? 'Removing…' : 'Remove' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ================== Help modal ================== -->
    <div class="modal fade" tabindex="-1" ref="helpModal" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="fas fa-info-circle me-2 text-primary"></i>Audit settings — Help</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeHelp"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted">
              These settings control how this client's accessibility audit runs. They're
              shared with the HeyGov dashboard — changing them here updates both places.
            </p>
            <dl class="ca-help-dl mb-0">
              <dt>Audit Engine</dt>
              <dd>
                <strong>Town Web Accessibility Checker</strong> — our own additional checks, always on.
                <strong>IBM Equal Access</strong> — code-level WCAG 2.2 AA checks.
                <strong>AXE</strong> — Deque axe-core engine.
                Checked engines run together and their findings are merged.
              </dd>
              <dt>Scan Interval</dt>
              <dd>
                How often an audit runs automatically (Off / Daily / Weekly / Monthly,
                plus a Dev “every 30 min” option for testing). Scheduled runs fire at
                2:00 AM Central. “Off” means manual only.
              </dd>
              <dt>Check only changed pages</dt>
              <dd>
                When on, scheduled runs re-scan only pages whose content changed since the
                last scan (and re-scan everything if the shared header/footer/menu changed).
                Unchanged pages reuse their previous result — faster, fewer engine calls.
              </dd>
              <dt>Run Audit</dt>
              <dd>
                Starts a full audit (all webpages) right now. The report appears in the list
                immediately with a progress bar and finishes on its own.
              </dd>
            </dl>

            <hr class="my-3" />

            <h6 class="fw-bold mb-1">Juniper ADA endpoints</h6>
            <p class="text-muted small mb-3">
              All under <code>Authorization: Bearer &lt;AUDIT_AUTH_TOKEN&gt;</code>.
              <code>{clientId}</code> is the juniper client id.
            </p>
            <div class="ca-endpoints">
              <div v-for="ep in juniperEndpoints" :key="ep.method + ep.path" class="ca-endpoint">
                <div class="ca-endpoint__head">
                  <span class="ca-endpoint__method" :class="'ca-endpoint__method--' + ep.method.toLowerCase()">
                    {{ ep.method }}
                  </span>
                  <code class="ca-endpoint__path">{{ ep.path }}</code>
                  <button
                    type="button"
                    class="ca-endpoint__copy"
                    title="Copy endpoint"
                    @click="copyEndpoint(ep.path)"
                  >
                    <i class="fas fa-copy"></i>
                  </button>
                </div>
                <div class="ca-endpoint__desc">{{ ep.desc }}</div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="closeHelp">Got it</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal, Tooltip } from 'bootstrap';
import api from '@/api';
import juniperApi from '@/api/juniperApi';
import { showGlobalToast } from '@/utils/toast';
import { fromNowUtc } from '@/utils/time';
import ScoreCircle from '@/components/juniper/ada-auditor/ScoreCircle.vue';
import ReportDetailsModal from '@/components/juniper/ada-auditor/ReportDetailsModal.vue';

/**
 * Audits tab for the juniper client settings page. Same gh-layout pattern as
 * every other Settings tab (aside intro + horizontal radio content). Save sits
 * bottom-right like every other tab; Run Audit lives in the Reports section.
 */
export default {
  name: 'ClientAuditsTab',
  components: { ScoreCircle, ReportDetailsModal },
  props: {
    clientId: { type: [Number, String], required: true },
    // juniper option main_dashboard — 'unified-dashboard' (HeyGov) or the classic
    // 'town-web-dashboard'. Decides how the report email is addressed.
    mainDashboard: { type: String, default: 'town-web-dashboard' },
  },
  data() {
    return {
      settings: {
        audit_engine: 'IBM',
        audit_scan_interval: 'none',
        audit_only_changed: false,
        audit_email_enabled: false,
        audit_email_recipients: '',
        audit_slack_enabled: false,
        audit_schedule_time: '',
        audit_schedule_weekday: '',
        audit_schedule_monthday: '',
      },
      // Multi-select engines (checkboxes). Persisted as a "+"-joined string in
      // audit_engine, canonical order TWAC, IBM, AXE (e.g. "TWAC+IBM+AXE").
      selectedEngines: ['TWAC', 'IBM'],
      saving: false,
      running: false,

      reports: [],
      total: 0,
      pageSize: 5,
      loadingReports: false,
      loadingMore: false,
      pollHandle: null,     // setInterval handle for auto-refresh
      pollIntervalMs: 5000,

      // Report details modal
      activeReportId: null,

      // Delete confirmation modal (bootstrap)
      deleteTarget: null,
      deleting: false,
      confirmModal: null,
      // Bulk-delete reports modal state
      bulkModal: null,
      bulkScope: 'all',
      bulkAge: '',
      bulkDeleting: false,

      // Resolutions (audit_warnings acknowledgments for this client)
      ackWarnings: [],
      loadingAcks: true,
      ackLimit: 10,
      ackDetailsModal: null,
      ackDetailsWarning: null,
      ackDetailsScan: null,
      ackDetailsExamples: [],
      ackDetailsLoading: false,
      ackDetailsError: null,
      ackDeleteModal: null,
      ackDeleteTarget: null,
      ackDeleting: false,

      // Help modal (bootstrap)
      helpModal: null,
      testEmailModal: null,
      scheduleModal: null,
      weekdayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      // All clients' scheduled slots (for the conflict warning); loaded once.
      scheduleSlots: [],
      testEmail: '',
      sendingTest: false,

      // Juniper-side ADA endpoints (all under the audit Bearer token), shown in Help.
      juniperEndpoints: [
        { method: 'GET',  path: '/api/audit/lookup-client?website=',            desc: 'Resolve a juniper client_id from a website (used by HeyGov).' },
        { method: 'GET',  path: '/api/audit/{clientId}/settings',               desc: 'Read saved audit settings (engine, interval, only-changed).' },
        { method: 'PUT',  path: '/api/audit/{clientId}/settings',               desc: 'Save audit settings into the client Options.' },
        { method: 'GET',  path: '/api/audit/{clientId}/urls',                   desc: 'All auditable webpage URLs (entity_type/id + updated_at).' },
        { method: 'GET',  path: '/api/audit/{clientId}/files',                  desc: 'Client files; add ?mime=application/pdf for PDFs only.' },
        { method: 'POST', path: '/api/audit/{clientId}/check-ibm',              desc: 'Single-URL IBM Equal Access check.' },
        { method: 'POST', path: '/api/audit/{clientId}/check-wave',             desc: 'Single-URL WAVE (WebAIM) check.' },
        { method: 'POST', path: '/api/audit/{clientId}/report',                 desc: 'Start a full audit report (engine read from Options).' },
        { method: 'GET',  path: '/api/audit/{clientId}/run-params',             desc: 'engine / only_changed / website / client_name for a run.' },
        { method: 'GET',  path: '/api/audit/{clientId}/layout-signature',       desc: 'Hash of shared header/footer/menu/theme (change detection).' },
        { method: 'GET',  path: '/api/audit/{clientId}/reports',                desc: 'Paginated list of audit reports.' },
        { method: 'GET',  path: '/api/audit/{clientId}/report/{reportId}',      desc: 'Single report with its scan rows.' },
      ],

      engineOptions: [
        { value: 'TWAC', label: 'TWAC', sub: 'Additional checks — always on', icon: 'fas fa-link', always: true },
        { value: 'IBM',  label: 'IBM Equal Access', sub: 'Code-level WCAG 2.2 AA',     icon: 'fas fa-code' },
        { value: 'AXE',  label: 'AXE (axe-core)',   sub: 'Deque axe-core engine',      icon: 'fas fa-universal-access' },
      ],
      intervalOptions: [
        { value: 'none',    label: 'Off',     sub: 'Manual only',  icon: 'fas fa-ban' },
        { value: 'daily',   label: 'Daily',   sub: 'Every day',    icon: 'fas fa-calendar-day' },
        { value: 'weekly',  label: 'Weekly',  sub: 'Every week',   icon: 'fas fa-calendar-week' },
        { value: 'monthly', label: 'Monthly', sub: 'Every month',  icon: 'fas fa-calendar-alt' },
        // Dev-only cadence (every 30 min) — intentionally NOT exposed in the
        // heygov dashboard settings modal; for internal testing of scheduled runs.
        { value: 'dev',     label: 'Dev',     sub: 'Every 30 min', icon: 'fas fa-flask' },
      ],
    };
  },
  computed: {
    isUnifiedDashboard() {
      return this.mainDashboard === 'unified-dashboard';
    },
    showSchedulePickers() {
      return ['daily', 'weekly', 'monthly'].includes(this.settings.audit_scan_interval);
    },
    intervalTitle() {
      return { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }[this.settings.audit_scan_interval] || '';
    },
    // Selected hour/minute from audit_schedule_time ('' = automatic → null).
    timeHour() {
      return this.settings.audit_schedule_time ? parseInt(this.settings.audit_schedule_time.split(':')[0], 10) : null;
    },
    timeMinute() {
      return this.settings.audit_schedule_time ? this.settings.audit_schedule_time.split(':')[1] : null;
    },
    // 'daily at 21:15 (automatic)' / 'every Saturday at 22:00' / 'monthly on day 15 …'
    scheduleSummary() {
      const t = this.settings.audit_schedule_time || `${this.autoSlotLabel} (automatic)`;
      const i = this.settings.audit_scan_interval;
      if (i === 'weekly') {
        const d = this.settings.audit_schedule_weekday !== ''
          ? this.weekdayNames[Number(this.settings.audit_schedule_weekday)]
          : `${this.autoWeekdayLabel} (automatic)`;
        return `every ${d} at ${t}`;
      }
      if (i === 'monthly') {
        const d = this.settings.audit_schedule_monthday !== ''
          ? `day ${this.settings.audit_schedule_monthday}`
          : `day ${this.autoMonthday} (automatic)`;
        return `monthly on ${d} at ${t}`;
      }
      return `daily at ${t}`;
    },
    // Automatic (hashed) slot — same crc32('ada-{id}') algorithm as the backend.
    autoHash() {
      return this.crc32(`ada-${this.clientId}`);
    },
    autoSlotLabel() {
      const slot = this.autoHash % 96;
      return `${String(Math.floor(slot / 4)).padStart(2, '0')}:${String((slot % 4) * 15).padStart(2, '0')}`;
    },
    autoWeekdayLabel() {
      return this.autoHash % 2 === 0 ? 'Saturday' : 'Sunday';
    },
    autoMonthday() {
      return (this.autoHash % 28) + 1;
    },
    // Other clients occupying my effective slot (day-aware): daily collides with
    // everything at that time; weekly/monthly only on the same day.
    slotConflicts() {
      if (!this.showSchedulePickers) return [];
      const myTime = this.settings.audit_schedule_time || this.autoSlotLabel;
      const myInterval = this.settings.audit_scan_interval;
      const myWeekday = this.settings.audit_schedule_weekday !== ''
        ? Number(this.settings.audit_schedule_weekday)
        : (this.autoHash % 2 === 0 ? 6 : 0);
      const myMonthday = this.settings.audit_schedule_monthday !== ''
        ? Number(this.settings.audit_schedule_monthday)
        : this.autoMonthday;
      return this.scheduleSlots
        .filter(o => String(o.client_id) !== String(this.clientId))
        .filter(o => o.time === myTime)
        .filter(o => {
          if (myInterval === 'daily' || o.interval === 'daily') return true;
          if (myInterval === 'weekly' && o.interval === 'weekly') return o.weekday === myWeekday;
          if (myInterval === 'monthly' && o.interval === 'monthly') return o.monthday === myMonthday;
          return false; // weekly vs monthly — occasional overlap, not worth warning
        })
        .map(o => `${o.name} (${o.interval} ${o.time})`);
    },
    remainingLoadCount() {
      return Math.min(this.pageSize, this.total - this.reports.length);
    },
    mc2Base() {
      return `/juniper/ada-auditor/clients/${this.clientId}`;
    },
    visibleAckWarnings() {
      return this.ackWarnings.slice(0, this.ackLimit);
    },
    // Bulk delete is a staff-only action (srdjan/marko).
    canBulkDelete() {
      const email = (this.$store.state.authUser && this.$store.state.authUser.email) || '';
      return email.includes('srdjan') || email.includes('marko');
    },
  },
  mounted() {
    this.loadSettings();
    this.loadReports(true);
    this.loadScheduleSlots();
    this.loadAckWarnings();
    // Poll silently every 5s so newly-finished runs + still-running ones update.
    this.pollHandle = setInterval(() => this.refreshReportsSilently(), this.pollIntervalMs);
    // Bootstrap delete-confirmation modal instance.
    if (this.$refs.deleteConfirmModal) {
      this.confirmModal = new Modal(this.$refs.deleteConfirmModal);
    }
    // Bootstrap bulk-delete-reports modal instance.
    if (this.$refs.bulkDeleteModal) {
      this.bulkModal = new Modal(this.$refs.bulkDeleteModal);
    }
    // Bootstrap help modal instance.
    if (this.$refs.helpModal) {
      this.helpModal = new Modal(this.$refs.helpModal);
    }
    // Bootstrap test-email modal instance.
    if (this.$refs.testEmailModal) {
      this.testEmailModal = new Modal(this.$refs.testEmailModal);
    }
    // Bootstrap run-schedule modal instance.
    if (this.$refs.scheduleModal) {
      this.scheduleModal = new Modal(this.$refs.scheduleModal);
    }
    // Resolution details + delete modal instances.
    if (this.$refs.ackDetailsModal) {
      this.ackDetailsModal = new Modal(this.$refs.ackDetailsModal);
    }
    if (this.$refs.ackDeleteModal) {
      this.ackDeleteModal = new Modal(this.$refs.ackDeleteModal);
    }
  },

  beforeDestroy() {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
    if (this.confirmModal) {
      this.confirmModal.dispose();
      this.confirmModal = null;
    }
    if (this.helpModal) {
      this.helpModal.dispose();
      this.helpModal = null;
    }
    if (this.testEmailModal) {
      this.testEmailModal.dispose();
      this.testEmailModal = null;
    }
    if (this.scheduleModal) {
      this.scheduleModal.dispose();
      this.scheduleModal = null;
    }
  },
  methods: {
    openHelp() {
      if (this.helpModal) this.helpModal.show();
    },
    // Standard CRC-32 (IEEE) — matches PHP's crc32() so the automatic slot
    // shown here is exactly the one the backend scheduler computes.
    crc32(str) {
      let crc = 0xFFFFFFFF;
      for (let i = 0; i < str.length; i++) {
        let c = (crc ^ str.charCodeAt(i)) & 0xFF;
        for (let k = 0; k < 8; k++) c = c & 1 ? (c >>> 1) ^ 0xEDB88320 : c >>> 1;
        crc = (crc >>> 8) ^ c;
      }
      return (crc ^ 0xFFFFFFFF) >>> 0;
    },
    openSchedule(interval) {
      this.settings.audit_scan_interval = interval;
      if (this.scheduleModal) this.scheduleModal.show();
    },
    closeSchedule() {
      if (this.scheduleModal) this.scheduleModal.hide();
    },
    setScheduleHour(h) {
      const m = this.timeMinute || '00';
      this.settings.audit_schedule_time = `${String(h).padStart(2, '0')}:${m}`;
    },
    setScheduleMinute(m) {
      // Minute picked before an hour → base it on the automatic slot's hour.
      const h = this.timeHour !== null ? this.timeHour : parseInt(this.autoSlotLabel.split(':')[0], 10);
      this.settings.audit_schedule_time = `${String(h).padStart(2, '0')}:${m}`;
    },
    async loadScheduleSlots() {
      try {
        const res = await juniperApi.get('/audit/schedule-slots');
        this.scheduleSlots = res?.data?.items || [];
      } catch (e) {
        console.error('loadScheduleSlots failed', e); // warning-only feature — non-fatal
      }
    },
    openTestEmail() {
      if (this.testEmailModal) this.testEmailModal.show();
    },
    closeTestEmail() {
      if (this.testEmailModal) this.testEmailModal.hide();
    },
    // Ask juniper to send a sample report email (design preview) to one address.
    async sendTestEmail() {
      if (!this.testEmail || this.sendingTest) return;
      this.sendingTest = true;
      try {
        await juniperApi.post(`/audit/${this.clientId}/report-email-test`, { email: this.testEmail });
        showGlobalToast(`Test report sent to ${this.testEmail}`, 'success');
        this.closeTestEmail();
      } catch (e) {
        console.error('sendTestEmail failed', e);
        const msg = e?.response?.data?.message || 'Failed to send test email';
        showGlobalToast(msg, 'danger');
      } finally {
        this.sendingTest = false;
      }
    },
    closeHelp() {
      if (this.helpModal) this.helpModal.hide();
    },
    copyEndpoint(path) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(path)
          .then(() => showGlobalToast('Endpoint copied', 'success'))
          .catch(() => showGlobalToast('Copy failed', 'danger'));
      } else {
        showGlobalToast('Copy not supported in this browser', 'danger');
      }
    },
    // Settings live in the standard juniper Options table — load + save go
    // directly through juniperApi, no mc2 proxy needed.
    async loadSettings() {
      try {
        // juniper Options "index" is a POST route (selector goes in the body).
        // This reads the very same audit_* rows that heygov writes via
        // /audit/{id}/settings, so both UIs stay in sync.
        const res = await juniperApi.post(`/options/${this.clientId}`, { selector: 'audit_' });
        const opts = res?.data?.options ?? res?.data ?? {};
        if (opts.audit_engine) {
          this.settings.audit_engine = opts.audit_engine;
          this.selectedEngines = this.parseEngines(opts.audit_engine);
        }
        if (opts.audit_scan_interval) this.settings.audit_scan_interval = opts.audit_scan_interval;
        if (opts.audit_email_enabled !== undefined) {
          this.settings.audit_email_enabled = opts.audit_email_enabled === '1' || opts.audit_email_enabled === 1 || opts.audit_email_enabled === true;
        }
        if (opts.audit_email_recipients !== undefined) {
          this.settings.audit_email_recipients = String(opts.audit_email_recipients || '');
        }
        if (opts.audit_slack_enabled !== undefined) {
          this.settings.audit_slack_enabled = opts.audit_slack_enabled === '1' || opts.audit_slack_enabled === 1 || opts.audit_slack_enabled === true;
        }
        if (opts.audit_schedule_time !== undefined) this.settings.audit_schedule_time = String(opts.audit_schedule_time || '');
        if (opts.audit_schedule_weekday !== undefined) this.settings.audit_schedule_weekday = String(opts.audit_schedule_weekday ?? '');
        if (opts.audit_schedule_monthday !== undefined) this.settings.audit_schedule_monthday = String(opts.audit_schedule_monthday ?? '');
        if (opts.audit_only_changed !== undefined) {
          // stored as '1'/'0' string in Options
          this.settings.audit_only_changed = opts.audit_only_changed === '1' || opts.audit_only_changed === 1 || opts.audit_only_changed === true;
        }
      } catch (e) {
        console.error('loadSettings failed', e);
      }
    },

    // Parse a stored engine string into a canonical-order array. TWAC is a
    // baseline that ALWAYS runs, so it's forced first regardless of the stored
    // value; IBM/AXE are optional. A brand-new client defaults to IBM.
    parseEngines(value) {
      const optionalOrder = ['IBM', 'AXE'];
      const raw = String(value || '').toUpperCase();
      const set = raw.split(/[+,]/).map(s => s.trim());
      const optional = optionalOrder.filter(e => set.includes(e));
      if (!raw && optional.length === 0) optional.push('IBM');
      return ['TWAC', ...optional];
    },
    // Canonical "+"-joined engine string. TWAC is always first + always included.
    engineString() {
      const optionalOrder = ['IBM', 'AXE'];
      const optional = optionalOrder.filter(e => this.selectedEngines.includes(e));
      return ['TWAC', ...optional].join('+');
    },

    async saveSettings() {
      this.saving = true;
      try {
        // Write goes to the dedicated "save" route — POST /options/{id}/save.
        // (POST /options/{id} is the read/index handler and ignores the body.)
        const engine = this.engineString();
        this.settings.audit_engine = engine;
        // Normalize recipients: trim entries, drop empties, join with ', '.
        const recipients = this.settings.audit_email_recipients
          .split(',')
          .map(e => e.trim())
          .filter(Boolean)
          .join(', ');
        this.settings.audit_email_recipients = recipients;
        await juniperApi.post(`/options/${this.clientId}/save`, {
          params: {
            audit_engine:        engine,
            audit_scan_interval: this.settings.audit_scan_interval,
            audit_only_changed:  this.settings.audit_only_changed ? '1' : '0',
            audit_email_enabled: this.settings.audit_email_enabled ? '1' : '0',
            audit_email_recipients: recipients,
            audit_slack_enabled: this.settings.audit_slack_enabled ? '1' : '0',
            audit_schedule_time: this.settings.audit_schedule_time,
            audit_schedule_weekday: this.settings.audit_schedule_weekday,
            audit_schedule_monthday: this.settings.audit_schedule_monthday,
          },
        });
        showGlobalToast('Audit settings saved', 'success');
      } catch (e) {
        console.error('saveSettings failed', e);
        showGlobalToast('Failed to save audit settings', 'danger');
      } finally {
        this.saving = false;
      }
    },

    async loadReports(reset = false) {
      if (reset) {
        this.reports = [];
        this.total   = 0;
      }
      const isInitial = this.reports.length === 0;
      isInitial ? (this.loadingReports = true) : (this.loadingMore = true);
      try {
        const res = await api.get(`${this.mc2Base}/audit-reports`, {
          params: { limit: this.pageSize, offset: this.reports.length },
        });
        if (res?.data?.items) {
          this.reports = [...this.reports, ...res.data.items];
          this.total   = res.data.total ?? this.reports.length;
        }
      } catch (e) {
        console.error('loadReports failed', e);
      } finally {
        this.loadingReports = false;
        this.loadingMore    = false;
        this.initReportTooltips();
      }
    },

    loadMore() {
      this.loadReports(false);
    },

    // ----- Bulk-delete reports -----
    openBulkDeleteModal() {
      if (this.bulkModal) this.bulkModal.show();
    },
    closeBulkDeleteModal() {
      if (this.bulkModal) this.bulkModal.hide();
    },
    async executeBulkDelete() {
      this.bulkDeleting = true;
      try {
        const payload = { scope: this.bulkScope };
        if (this.bulkAge) payload.older_than_days = Number(this.bulkAge);
        const res = await api.post(`${this.mc2Base}/audit-reports/bulk-delete`, payload);
        const n = res?.data?.deleted || 0;
        showGlobalToast(`Deleted ${n} report${n === 1 ? '' : 's'}`, 'success');
        this.closeBulkDeleteModal();
        await this.loadReports(true);
      } catch (e) {
        console.error('Bulk delete failed', e);
        showGlobalToast(e?.response?.data?.message || 'Failed to delete reports', 'danger');
      } finally {
        this.bulkDeleting = false;
      }
    },

    // Auto-refresh tick: re-fetches the same window the user is currently looking
    // at (keeps their "Load more" expansion). Does NOT toggle loading flags, so
    // the table stays put and only the data swaps in.
    async refreshReportsSilently() {
      try {
        const limit = Math.max(this.pageSize, this.reports.length);
        const res = await api.get(`${this.mc2Base}/audit-reports`, {
          params: { limit, offset: 0 },
        });
        if (res?.data?.items) {
          this.reports = res.data.items;
          this.total   = res.data.total ?? this.reports.length;
        }
      } catch (e) {
        // Polling failures are non-fatal — log and try again next tick.
        console.warn('audit-reports auto-refresh failed', e);
      }
    },

    // Read scans.{webpages|files} as { total, scanned }.
    // Tolerates three shapes:
    //   - null/missing                  → { total: 0, scanned: 0 }
    //   - number (legacy reports)       → { total: N, scanned: N } (already finished)
    //   - { total, scanned }            → returned as-is
    scansOf(r, key) {
      const v = r?.scans?.[key];
      if (v === undefined || v === null) return { total: 0, scanned: 0, reused: 0 };
      if (typeof v === 'number')         return { total: v, scanned: v, reused: 0 };
      return { total: Number(v.total || 0), scanned: Number(v.scanned || 0), reused: Number(v.reused || 0) };
    },

    // Overall progress: combined (scanned / total) across all scan kinds.
    progressOf(r) {
      const w = this.scansOf(r, 'webpages');
      const f = this.scansOf(r, 'files');
      const total   = w.total   + f.total;
      const scanned = w.scanned + f.scanned;
      if (total <= 0) return 0;
      return Math.min(100, Math.max(0, Math.round((scanned / total) * 100)));
    },

    // Empty body — juniper reads the engine from the saved Options (scope = webpages).
    async runAudit() {
      this.running = true;
      try {
        await api.post(`${this.mc2Base}/audit-run`, {});
        showGlobalToast('Audit started — it will appear in the list as soon as it begins.', 'success');
        await this.loadReports(true);
      } catch (e) {
        console.error('runAudit failed', e);
        showGlobalToast('Failed to start audit', 'danger');
      } finally {
        this.running = false;
      }
    },

    downloadReport(/* r */) {
      showGlobalToast('Download is not implemented yet.', 'info');
    },

    // Open the bootstrap confirmation modal for a given report.
    askDelete(r) {
      this.deleteTarget = r;
      if (this.confirmModal) this.confirmModal.show();
    },
    closeDeleteModal() {
      if (this.confirmModal) this.confirmModal.hide();
    },
    async confirmDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      const id = this.deleteTarget.id;
      try {
        await api.delete(`${this.mc2Base}/audit-reports/${id}`);
        this.reports = this.reports.filter(x => x.id !== id);
        if (this.total > 0) this.total -= 1;
        showGlobalToast('Report deleted', 'success');
        if (this.confirmModal) this.confirmModal.hide();
        this.deleteTarget = null;
      } catch (e) {
        console.error('deleteReport failed', e);
        showGlobalToast('Failed to delete report', 'danger');
      } finally {
        this.deleting = false;
      }
    },

    openReport(id) {
      this.activeReportId = id;
    },
    closeReport() {
      this.activeReportId = null;
    },

    formatDate(s) {
      if (!s) return '';
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    // triggered_by is a { id, name, email, from } object now; show the name.
    // Legacy rows decode to null → blank cell.
    generatedByName(r) {
      const g = r && r.triggered_by;
      if (g && typeof g === 'object') return g.name || g.email || '';
      return '';
    },

    // System-generated (scheduled/automatic) reports have no real user —
    // triggered_by is null, has no id, or its source is automatic/system.
    isSystemGenerated(r) {
      const g = r && r.triggered_by;
      if (!g || typeof g !== 'object') return true;
      const from = (g.from || '').toLowerCase();
      if (from === 'automatic' || from === 'system' || from === 'cron') return true;
      const name = (g.name || '').toLowerCase();
      if (name === 'system' || name === 'automatic') return true;
      return !g.id && !g.email;
    },
    triggeredByTooltip(r) {
      if (this.isSystemGenerated(r)) return 'System (scheduled)';
      const name = this.generatedByName(r);
      return name ? 'Triggered by ' + name : 'Triggered by user';
    },

    fromNowUtc,

    // ---------- Resolutions (audit_warnings acknowledgments) ----------
    async loadAckWarnings() {
      this.loadingAcks = true;
      try {
        const res = await api.get('/juniper/ada-auditor/warnings', {
          params: { client_id: this.clientId, include_global: 0 },
        });
        this.ackWarnings = res.data.client || [];
      } catch (e) {
        console.error('resolutions load failed', e);
      } finally {
        this.loadingAcks = false;
        this.initReportTooltips();
      }
    },
    ackTooltip(w) {
      const by = w.acknowledged_by;
      if (!by) return 'Unknown user';
      const name = by.name || by.email || 'Unknown user';
      return by.email && by.name ? `${name} · ${by.email}` : name;
    },
    async openAckDetails(w) {
      this.ackDetailsWarning = w;
      this.ackDetailsScan = null;
      this.ackDetailsExamples = [];
      this.ackDetailsError = null;
      this.ackDetailsLoading = true;
      this.ackDetailsModal.show();
      try {
        const res = await api.get(`/juniper/ada-auditor/warnings/${w.id}/details`);
        this.ackDetailsScan = res.data.scan;
        this.ackDetailsExamples = res.data.examples || [];
      } catch (e) {
        console.error('resolution details failed', e);
        this.ackDetailsError = 'Failed to load details.';
      } finally {
        this.ackDetailsLoading = false;
      }
    },
    closeAckDetails() {
      this.ackDetailsModal.hide();
    },
    ackExampleLocation(ex) {
      const raw = ex.raw || {};
      const sel = Array.isArray(ex.selectors) ? ex.selectors.join(', ') : ex.selectors;
      return ex.xpath || ex.selector || sel || ex.target || raw.xpath || raw.target || '';
    },
    ackExampleSnippet(ex) {
      return ex.snippet || (ex.raw && ex.raw.snippet) || '';
    },
    loadMoreAcks() {
      this.ackLimit += 10;
      this.initReportTooltips();
    },
    askAckDelete(w) {
      this.ackDeleteTarget = w;
      this.ackDeleteModal.show();
    },
    closeAckDelete() {
      this.ackDeleteModal.hide();
      this.ackDeleteTarget = null;
    },
    async confirmAckDelete() {
      if (!this.ackDeleteTarget) return;
      this.ackDeleting = true;
      try {
        await api.delete(`/juniper/ada-auditor/warnings/${this.ackDeleteTarget.id}`);
        this.ackWarnings = this.ackWarnings.filter((x) => x.id !== this.ackDeleteTarget.id);
        showGlobalToast('Resolution removed — finding will re-surface on the next scan', 'success');
        this.ackDeleteModal.hide();
      } catch (e) {
        console.error('resolution delete failed', e);
        showGlobalToast('Failed to remove resolution', 'error');
      } finally {
        this.ackDeleting = false;
        this.ackDeleteTarget = null;
      }
    },

    // (Re)attach Bootstrap tooltips on the reports table. Rows re-render on
    // every load, so stale instances are disposed first.
    initReportTooltips() {
      this.$nextTick(() => {
        this.$el.querySelectorAll('.cs-subs-table [data-bs-toggle="tooltip"]').forEach((el) => {
          const existing = Tooltip.getInstance(el);
          if (existing) existing.dispose();
          new Tooltip(el);
        });
      });
    },
  },
};
</script>

<style scoped>
/* ------------------------------------------------------------------
   Local copies of the gh-* layout classes so this component matches
   the rest of the Settings tabs (parent CSS is scoped, doesn't leak in).
   Values mirror $gh-* SCSS variables from ClientSettings.vue.
   ------------------------------------------------------------------ */
.ca-tab { padding: 0.5rem 0; }

.gh-layout {
  display: grid;
  grid-template-columns: 30% 1fr;
  gap: 1.5rem;
  align-items: start;
}

.gh-intro { padding: 0.25rem 1rem 0 0; }

.gh-intro__title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2b303b;
  margin: 0 0 0.5rem;
  letter-spacing: -0.01em;
}

/* "Audit" pill next to the section title — mirrors gh-intro__badge */
.gh-intro__title .gh-intro__badge {
  display: inline-block;
  vertical-align: middle;
  margin-left: 0.45rem;
  padding: 0.22rem 0.6rem;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.02em;
  color: #6b7280;
  background: #f3f5f9;
  border-radius: 999px;
  white-space: nowrap;
  position: relative;
  top: -0.15em;
}

.gh-intro__desc {
  font-size: 0.85rem;
  color: #8a909c;
  line-height: 1.5;
  margin: 0;
}

.ca-intro-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.6rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: #4a5260;
  text-decoration: none;
}
.ca-intro-link:hover { color: #2b6cff; }
.ca-intro-link__arrow { font-size: 0.65rem; }

.gh-accounts { display: flex; flex-direction: column; }

.gh-divider {
  border: none;
  border-top: 1px solid #c8ccd4;
  margin: 1.5rem 15px;
}

.gh-save-row {
  display: flex;
  justify-content: flex-end;
}
/* Help button far-left, Save far-right */
.gh-save-row--split {
  justify-content: space-between;
  align-items: center;
}

/* Round info/help icon button (far left of the Save row) */
.ca-help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  background: transparent;
  border: 1px solid #e6e8ec;
  border-radius: 50%;
  color: #6b7280;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.ca-help-btn:hover { background: #ebf2ff; border-color: #b6cfff; color: #2b6cff; }

/* Help modal definition list */
.ca-help-dl dt { font-weight: 700; color: #2b303b; margin-top: 0.75rem; font-size: 0.92rem; }
.ca-help-dl dt:first-child { margin-top: 0; }
.ca-help-dl dd { margin: 0.15rem 0 0; color: #6b7280; font-size: 0.85rem; line-height: 1.5; }
.ca-help-dl dd strong { color: #2b303b; }

/* Endpoint list in the Help modal */
.ca-endpoints { display: flex; flex-direction: column; gap: 0.5rem; }
.ca-endpoint {
  border: 1px solid #eef0f4;
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  background: #fbfcfe;
}
.ca-endpoint__head { display: flex; align-items: center; gap: 0.5rem; }
.ca-endpoint__method {
  flex-shrink: 0;
  min-width: 42px;
  text-align: center;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 0.12rem 0.4rem;
  border-radius: 5px;
  color: #fff;
}
.ca-endpoint__method--get  { background: #1fc16b; }
.ca-endpoint__method--post { background: #2b6cff; }
.ca-endpoint__method--put  { background: #ff8447; }
.ca-endpoint__path {
  flex: 1;
  font-size: 0.78rem;
  color: #2b303b;
  word-break: break-all;
  background: transparent;
  padding: 0;
}
.ca-endpoint__copy {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid #e6e8ec;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  font-size: 0.78rem;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.ca-endpoint__copy:hover { background: #ebf2ff; border-color: #b6cfff; color: #2b6cff; }
.ca-endpoint__desc { font-size: 0.76rem; color: #8a909c; margin-top: 0.3rem; }

.gh-save-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.95rem;
  background: transparent;
  border: 1px solid #e6e8ec;
  border-radius: 10px;
  color: #2b303b;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.gh-save-btn:hover:not(:disabled) { background: #f3f5f9; border-color: #d8dce4; }
.gh-save-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.gh-save-btn--primary {
  background: #2b6cff;
  border-color: #2b6cff;
  color: #fff;
}
.gh-save-btn--primary:hover:not(:disabled) { background: #2456cf; border-color: #2456cf; }

/* Deploy-style horizontal radio (3 columns) */
.ca-target-group {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
}
@media (max-width: 991px) {
  .ca-target-group { grid-template-columns: 1fr; }
}

.ca-target-opt {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.85rem 0.9rem;
  border: 1px solid #e6e8ec;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  user-select: none;
  transition: all 0.12s ease;
  margin: 0;
}
.ca-target-opt input { display: none; }
/* Small checkbox indicator (engine multi-select) */
.ca-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 1.5px solid #cfd4dc;
  border-radius: 5px;
  background: #fff;
  color: #fff;
  font-size: 0.6rem;
}
.ca-check--on { background: #2b6cff; border-color: #2b6cff; }
.ca-target-opt i {
  font-size: 1.1rem;
  color: #8a909c;
  width: 22px;
  text-align: center;
}
.ca-target-opt:hover { background: #f3f5f9; }
.ca-target-opt:hover i { color: #2b303b; }
.ca-target-opt--active { border-color: #b6cfff; background: #ebf2ff; }
.ca-target-opt--active i { color: #2b6cff; }
.ca-target-opt--active .ca-target-opt__name { color: #2b6cff; }
.ca-target-opt__name { font-size: 0.9rem; font-weight: 600; color: #2b303b; }
.ca-target-opt__sub  { font-size: 0.72rem; color: #8a909c; }

/* Toggle ("check only changed") */
.ca-toggle-row { margin-top: 0.85rem; }
.ca-toggle-row--first { margin-top: 0; }
.ca-email-note {
  margin-top: 0.85rem;
  padding: 0.6rem 0.85rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
  color: #4b5563;
  font-size: 0.85rem;
}
.ca-email-note i { margin-right: 0.35rem; color: #6b7280; }
.ca-email-recipients { margin-top: 0.85rem; }
.ca-email-recipients label {
  display: block;
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.3rem;
}
.ca-email-recipients small { display: block; margin-top: 0.3rem; }
.ca-test-email-row { margin-top: 1rem; }
.ca-gear {
  margin-left: auto;
  border: 0;
  background: none;
  color: #9ca3af;
  padding: 0.25rem 0.35rem;
  border-radius: 6px;
  cursor: pointer;
}
.ca-gear:hover { color: #374151; background: #f3f4f6; }
.ca-schedule-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.85rem;
  font-size: 0.85rem;
  color: #4b5563;
}
.ca-schedule-summary__edit {
  border: 0;
  background: none;
  color: #2563eb;
  font-size: 0.85rem;
  padding: 0;
  cursor: pointer;
}
.ca-schedule-summary__edit:hover { text-decoration: underline; }
.ca-schedule-summary__conflict { color: #92400e; }
.ca-picker-label {
  font-weight: 600;
  font-size: 0.85rem;
  margin: 0.75rem 0 0.4rem;
}
.ca-picker-label:first-child { margin-top: 0; }
.ca-chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.ca-chip {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  border-radius: 999px;
  padding: 0.3rem 0.65rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.12s ease;
}
.ca-chip:hover { border-color: #94a3b8; }
.ca-chip--on {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  font-weight: 600;
}
.ca-chip--disabled { opacity: 0.45; }
.ca-grid { display: grid; gap: 0.35rem; margin-top: 0.35rem; }
.ca-grid .ca-chip { border-radius: 8px; text-align: center; padding: 0.3rem 0; }
.ca-grid--hours { grid-template-columns: repeat(8, 1fr); }
.ca-grid--days { grid-template-columns: repeat(7, 1fr); }
.ca-chips--minutes { margin-top: 0.35rem; }
.ca-slot-warning {
  margin-top: 0.6rem;
  padding: 0.55rem 0.8rem;
  border: 1px solid #fde68a;
  border-radius: 8px;
  background: #fffbeb;
  color: #92400e;
  font-size: 0.85rem;
}
.ca-slot-warning i { margin-right: 0.35rem; }
.ca-test-email-row small { margin-top: 0.35rem; }
.ca-toggle {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  cursor: pointer;
  margin: 0;
}
.ca-toggle input { display: none; }
.ca-toggle__track {
  position: relative;
  width: 38px;
  height: 22px;
  background: #cfd4dc;
  border-radius: 999px;
  flex-shrink: 0;
  transition: background 0.15s ease;
  margin-top: 1px;
}
.ca-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.15s ease;
}
.ca-toggle input:checked + .ca-toggle__track { background: #2b6cff; }
.ca-toggle input:checked + .ca-toggle__track .ca-toggle__thumb { transform: translateX(16px); }
.ca-toggle__label { font-size: 0.9rem; font-weight: 600; color: #2b303b; line-height: 1.25; }
.ca-toggle__label small { font-weight: 400; }

/* Reports toolbar — Run lives here, top-right of the Reports section */
.ca-reports-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

/* Same button language as the Deploy page (cd-btn) */
.ca-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.9rem;
  border: 1px solid #e6e8ec;
  border-radius: 9px;
  font-size: 0.85rem;
  font-weight: 600;
  background: transparent;
  color: #2b303b;
  cursor: pointer;
  transition: all 0.12s ease;
}

.ca-btn:hover:not(:disabled) {
  background: #f3f5f9;
}

.ca-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ca-btn--danger {
  color: #dc2626;
  border-color: #fecaca;
}

.ca-btn--danger:hover:not(:disabled) {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

/* Compact toolbar variant — matches cd-btn--sm on the Deploy page. */
.ca-btn--sm {
  padding: 0.25rem 0.6rem;
  font-size: 0.78rem;
  border-radius: 7px;
  gap: 0.3rem;
}

/* ------------------------------------------------------------------
   Subscribers-list look (copied from ClientSettings.vue with concrete
   hex values in place of the $gh-* SCSS variables that don't reach here).
   ------------------------------------------------------------------ */

/* Count pill next to the section title ("Reports  Audit  12") */
.cs-count-badge {
  display: inline-block;
  vertical-align: middle;
  margin-left: 0.5rem;
  padding: 0.18rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  color: #2b6cff;
  background: #ebf2ff;
  border-radius: 999px;
  position: relative;
  top: -0.25em;
}

/* Card wrapper around the table */
.ada-card {
  border: 1px solid #e6e8ec;
  border-radius: 12px;
  padding: 1rem 1.1rem;
  background: transparent;
}

.cs-empty-state {
  text-align: center;
  padding: 1.25rem 1rem;
  color: #8a909c;
  font-size: 0.9rem;
}

/* Borderless table with subtle row separators + hover row */
/* Compact flat table — same design as the settings Hooks "Recent activity"
   list: plain 13px rows, thin separators, no header background. */
.cs-subs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.cs-subs-table thead th {
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  color: #6c757d;
  padding: 4px 10px 4px 0;
  border: none;
  border-bottom: 1px solid #dee2e6;
  background: transparent;
}
.cs-subs-table tbody td {
  padding: 6px 10px 6px 0;
  color: #2b303b;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: middle;
}
.cs-subs-table tbody tr:last-child td { border-bottom: none; }

.cs-subs-table__primary { font-weight: 600; }

/* Whole row is clickable → opens the report details modal. */
.ca-row { cursor: pointer; }
.ca-row:hover td { background: #f3f5f9; }

/* Name cell — plain gray text (not a bold blue link anymore). */
.ca-report-name {
  color: #4a5260;
  word-break: break-word;
}

/* Shared row icon — same size/tone as the files metric icon on the
   Deploy page, so every icon in a row lines up. */
.ca-row-icon {
  font-size: 0.78rem;
  color: #8a909c;
  cursor: help;
}

.cs-subs-table__score-col   { width: 4rem; text-align: center; white-space: nowrap; }
.cs-subs-table__actions-col { width: 4.5rem; }
.ca-actions { display: inline-flex; align-items: center; gap: 0.5rem; flex-wrap: nowrap; justify-content: flex-end; }

/* Webpages column — file icon + scanned/total, sync icon + reused count */
.ca-scans-col { width: 9rem; }
.ca-scans__row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 13px;
  color: #4a5260;
}

/* Status pill — only used while a report is still running */
.cs-status-pill {
  display: inline-block;
  padding: 0.18rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
  border-radius: 999px;
  text-transform: capitalize;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.cs-status-pill--pending {
  color: #a16207;
  background: #fef3c7;
}

/* Linear progress bar — shown in the Score column while a report is running,
   replaces the old "running" pill + spinner. % is computed from
   (webpages.scanned + files.scanned) / (webpages.total + files.total). */
.ca-progress {
  position: relative;
  width: 64px;
  height: 18px;
  background: #f0f2f6;
  border-radius: 999px;
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
}
.ca-progress__bar {
  position: absolute;
  inset: 0 auto 0 0;
  background: #2b6cff;          /* mc2 primary blue */
  border-radius: 999px;
  transition: width 0.4s ease;
}
.ca-progress__label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: #2b303b;
  line-height: 1;
  text-shadow: 0 0 2px rgba(255,255,255,0.85);
}

/* Plain gray icon actions — no border/background/padding, gray like the
   delete icon in the settings Hooks "Recent activity" table; colour on hover. */
.ca-link-icon {
  border: 0;
  background: none;
  padding: 2px 4px;
  color: #adb5bd;
  cursor: pointer;
  transition: color 0.12s ease;
}
.ca-link-icon:hover { color: #2b303b; }
.ca-link-icon--danger:hover { color: #dc3545; }

/* Load-more action row — one compact centered line: [Load more] 5 of 12 */
.cs-load-more-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 0 0.25rem;
}
/* Plain text action — no border/background/padding, gray like the delete
   icon in the Hooks "Recent activity" table. */
.cs-load-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: 0;
  padding: 0;
  color: #adb5bd;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.12s ease;
}
.cs-load-more-btn:hover:not(:disabled) { color: #2b303b; }
.cs-load-more-btn:disabled { opacity: 0.55; cursor: default; }
.cs-load-more-btn small { font-weight: 500; font-size: 0.72rem; opacity: 0.85; }

/* ---------- Resolutions table (audit_warnings acknowledgments) ---------- */
.ca-ack-engine {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
}
.ca-ack-rule {
  font-size: 12px;
  color: #2b303b;
  overflow-wrap: anywhere;
}
.ca-ack-url {
  font-size: 11.5px;
  color: #8a919c;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ca-ack-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 0.1rem 0.45rem;
  border-radius: 10px;
}
.ca-ack-badge--error { background: #fdecec; color: #d64545; }
.ca-ack-badge--warn { background: #fdf3e0; color: #b47a0b; }
.ca-ack-badge--dismissed { background: #f0f2f5; color: #5b6472; }
.ca-ack-badge--resolved { background: #e7f6ee; color: #1a9e55; }
.ca-ack-note { max-width: 220px; }
.ca-ack-note span {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
.ca-ack-added { white-space: nowrap; }
.ca-ack-added .ca-row-icon { margin-right: 0.35rem; font-size: 15px; }

/* Resolution details modal */
.ca-ackd-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 0.9rem;
}
.ca-ackd-body { font-size: 13px; }
.ca-ackd-note {
  background: #f8f9fb;
  border: 1px solid #f1f3f5;
  border-radius: 8px;
  padding: 0.5rem 0.7rem;
  margin-bottom: 0.5rem;
}
.ca-ackd-meta {
  color: #8a919c;
  font-size: 12px;
  margin-bottom: 0.75rem;
  overflow-wrap: anywhere;
}
.ca-ackd-scan { font-size: 12.5px; margin-bottom: 0.6rem; }
.ca-ackd-scan a { color: #2b303b; overflow-wrap: anywhere; }
.ca-ackd-examples { list-style: none; margin: 0; padding: 0; }
.ca-ackd-example { padding: 0.6rem 0; border-top: 1px solid #f1f3f5; }
.ca-ackd-example-msg { font-weight: 500; }
.ca-ackd-example-loc { margin-top: 0.25rem; }
.ca-ackd-example-loc code {
  font-size: 11.5px;
  color: #8a919c;
  overflow-wrap: anywhere;
}
.ca-ackd-example-snippet {
  margin: 0.35rem 0 0;
  padding: 0.45rem 0.6rem;
  background: #f8f9fb;
  border: 1px solid #f1f3f5;
  border-radius: 8px;
  font-size: 11.5px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 140px;
  overflow-y: auto;
}
</style>
